// NOTE: Token is stored in localStorage for PWA offline support.
// This is a conscious trade-off — HTTP-only cookies would be more secure against XSS,
// but the offline queue needs access to the token for syncing transactions.
// The signed JWT (HMAC-SHA256) mitigates the risk by making stolen tokens expire
// and preventing token forgery.

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api } from '../lib/api'
import { idb, STORES } from '../lib/idb'
import { getPendingCount, syncAllPending } from '../lib/offlineQueue'
import type { LoginResponse } from '@van-sales/shared'

interface User {
  user_id: string
  name: string
  role: 'admin' | 'sales_staff'
}

interface Tenant {
  id: string
  name: string
}

interface AuthContextType {
  user: User | null
  tenant: Tenant | null
  token: string | null
  isLoading: boolean
  login: (identifier: string, password: string) => Promise<void>
  /** User-initiated logout. Async because it may prompt-to-confirm when
   *  there are unsynced offline transactions. Returns true if the user
   *  actually logged out, false if they cancelled the confirm dialog. */
  logout: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'))
  const [isLoading, setIsLoading] = useState(true)

  // Internal: clears all client state. Caller must do the "should we
  // really do this?" gating (see `logout` below).
  //
  // preserveQueue: keep queued offline transactions. Used for FORCED
  // logouts (session expiry) — the driver's unsynced sales are still
  // valid work; they sync after re-login. Cross-user replay is already
  // prevented at sync time by the owner_user_id/owner_tenant_id stamps
  // on each queued transaction. Only an EXPLICIT logout (which warns the
  // user first) drops the queue.
  const performLogout = useCallback((preserveQueue = false) => {
    setUser(null)
    setTenant(null)
    setToken(null)
    localStorage.removeItem('token')
    localStorage.removeItem('tenant_id')
    localStorage.removeItem('user_id')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('auth_tenant')
    // Clear cached data to prevent data leakage between users
    idb.clear(STORES.CUSTOMERS).catch(() => {})
    idb.clear(STORES.PRODUCTS).catch(() => {})
    // Added with the offline work and easy to forget here: this store
    // holds the catalogue WITH COSTS, and the offline cache no longer
    // expires — so leaving it would hand the next user on this device the
    // previous tenant's buying prices, indefinitely.
    idb.clear(STORES.PRODUCTS_WITH_UOMS).catch(() => {})
    idb.clear(STORES.SUPPLIERS).catch(() => {})
    idb.clear(STORES.INVENTORY).catch(() => {})
    idb.clear(STORES.CACHE_META).catch(() => {})
    if (!preserveQueue) {
      // Explicit logout: drop queued offline transactions — the user was
      // warned and chose to proceed.
      idb.clear(STORES.PENDING_TRANSACTIONS).catch(() => {})
    }
  }, [])

  const logout = useCallback(async (): Promise<boolean> => {
    // Field staff sometimes log out at end-of-shift while still offline —
    // and the offline queue gets cleared by performLogout, silently
    // destroying uncommitted sales. Warn them first.
    try {
      const pending = await getPendingCount()
      if (pending > 0) {
        const proceed = confirm(
          `You have ${pending} unsynced ${pending === 1 ? 'transaction' : 'transactions'}. ` +
          `Logging out will permanently delete them.\n\n` +
          `Tip: connect to wifi/data first to sync, then log out.\n\n` +
          `Log out anyway?`
        )
        if (!proceed) return false
      }
    } catch {
      // The queue could not be READ — which is exactly when we know least
      // about what is about to be deleted. Staying silent here meant a
      // driver whose IndexedDB was unavailable (blocked upgrade, quota,
      // private mode) was logged out and had their unsynced sales cleared
      // without ever being told there were any. Warn on the uncertainty.
      const proceed = confirm(
        `Could not check for unsynced transactions on this device.\n\n` +
        `If any sale or payment has not reached the server yet, logging out will ` +
        `permanently delete it.\n\n` +
        `Tip: connect to wifi/data first so anything pending can sync.\n\n` +
        `Log out anyway?`
      )
      if (!proceed) return false
    }
    performLogout()
    return true
  }, [performLogout])

  // Server-signalled session expiry (api.ts emits this on any 401).
  // Forced logout, but PRESERVE the offline queue: the classic failure
  // was a driver's 14h token lapsing mid-shift — the first sync attempt
  // 401'd, this handler fired, and performLogout wiped every unsynced
  // sale from the device. Preserving the queue lets the same driver log
  // back in and sync; a different user is blocked by the per-transaction
  // owner stamps in offlineQueue.
  useEffect(() => {
    const onExpired = () => performLogout(true)
    window.addEventListener('auth:expired', onExpired)
    return () => window.removeEventListener('auth:expired', onExpired)
  }, [performLogout])

  /** A failure that means "we could not reach the server", as opposed to
   *  the server telling us the token is no good. Only the latter is a
   *  reason to sign someone out. */
  const isUnreachable = (r: { success: false; error?: { code?: string } }) =>
    r.error?.code === 'OFFLINE' || r.error?.code === 'NETWORK_ERROR' || r.error?.code === 'TIMEOUT'

  /** Bring the session back from the profile cached at the last successful
   *  verify, so a driver stays signed in with no coverage. The next online
   *  request re-validates: api.ts fires auth:expired on a real 401, which
   *  logs out for genuine auth failures. */
  const restoreCachedSession = useCallback(() => {
    const cachedUser = localStorage.getItem('auth_user')
    const cachedTenant = localStorage.getItem('auth_tenant')
    if (!cachedUser || !cachedTenant) return   // never verified here; stay logged out, keep the queue
    try {
      setUser(JSON.parse(cachedUser) as User)
      setTenant(JSON.parse(cachedTenant) as Tenant)
    } catch {
      performLogout(true)   // corrupt cache — not a user-chosen logout, so keep the queue
    }
  }, [performLogout])

  // Verify token on mount
  useEffect(() => {
    async function verifyToken() {
      if (!token) {
        setIsLoading(false)
        return
      }

      try {
        const response = await api.get<{ user: User; tenant: Tenant }>('/auth/verify')
        if (response.success) {
          setUser(response.data.user)
          setTenant(response.data.tenant)
          // Cache the verified profile so a later offline cold-load can
          // restore the session without hitting the network (see catch below).
          localStorage.setItem('tenant_id', response.data.tenant.id)
          localStorage.setItem('user_id', response.data.user.user_id)
          localStorage.setItem('auth_user', JSON.stringify(response.data.user))
          localStorage.setItem('auth_tenant', JSON.stringify(response.data.tenant))
          // Warm the offline caches in the background so unvisited Field
          // pages still work after going offline. Without this, the first
          // navigation to (say) VanInventory while offline shows empty
          // because the cache only populated lazily on prior visits.
          api.preCacheData().catch(() => { /* silent — opportunistic */ })
        } else if (isUnreachable(response)) {
          // COULD NOT ASK — not an answer. api.get does not throw when the
          // device is offline or the request fails to leave; it RETURNS
          // success:false, so this landed in the logout branch below and
          // signed the driver out the moment they lost coverage. Worse, it
          // cleared the cached profile too, so they could not get back in
          // until signal returned — in a van, exactly when they cannot.
          restoreCachedSession()
        } else {
          // Token invalid or expired (server returned a real 401/403) —
          // clean up and redirect to login. performLogout (not logout)
          // because we never want a "are you sure?" prompt on a server-
          // initiated invalidation. Queue preserved: expired-session
          // logouts must never destroy unsynced sales.
          performLogout(true)
        }
      } catch {
        // Threw rather than returned — same meaning, same handling.
        restoreCachedSession()
      } finally {
        setIsLoading(false)
      }
    }

    verifyToken()
  }, [token, performLogout, restoreCachedSession])

  const login = async (identifier: string, password: string) => {
    const response = await api.post<LoginResponse>('/auth/login', { identifier, password })

    if (response.success) {
      const { token: newToken, user: newUser, tenant: newTenant } = response.data
      localStorage.setItem('token', newToken)
      localStorage.setItem('tenant_id', newTenant.id)
      localStorage.setItem('user_id', newUser.user_id)
      localStorage.setItem('auth_user', JSON.stringify(newUser))
      localStorage.setItem('auth_tenant', JSON.stringify(newTenant))
      api.preCacheData().catch(() => { /* silent — opportunistic */ })
      setToken(newToken)
      setUser(newUser)
      setTenant(newTenant)
      // Drain anything queued under the PREVIOUS token. A driver offline
      // past the session's life comes back, the sync fires, every write
      // 401s, and they are logged out with the day's sales still queued —
      // and nothing then retried, because the only triggers were the
      // 'online' event (already fired) and a manual tap. Logging back in
      // is exactly the moment those writes can finally succeed.
      syncAllPending().catch(() => { /* the banner surfaces failures */ })
    } else {
      throw new Error(response.error.message)
    }
  }

  return (
    <AuthContext.Provider value={{ user, tenant, token, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
